/* eslint-disable max-len */
import { isEmpty } from "lodash";
import { grey } from "@mui/material/colors";
import { EPresenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  IAbsenceType,
  IEmployeeBalances,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

/**
 * Function transforming balances data from API to structure ready for rendering in two lists (active and inactive),
 * each containing absenceTypes and attendanceTypes. It adds badge color from group config to absence type,
 * moves secondary absence type to its parent `children` property and filters out data unnecessary for render.
 * @param {object} data - A collection of absence types available for given employee with their balance statuses.
 */
export default (data: IEmployeeBalances) => {
  if (!data || isEmpty(data))
    return {
      active: { absenceTypes: [], attendanceTypes: [] },
      inactive: { absenceTypes: [], attendanceTypes: [] },
    };

  const { balanceDetails, absenceGroupInfos } = data;

  const groupInfo = Object.fromEntries(
    absenceGroupInfos.map((group) => [
      group.id,
      { color: group.color, order: group.order },
    ])
  );
  const entriesMap: { [key: string]: IAbsenceType } = {};
  const resultArray: IAbsenceType[] = [];

  const sortByGroupOrder = (a: IAbsenceType, b: IAbsenceType) =>
    (a.order ?? Infinity) - (b.order ?? Infinity);

  balanceDetails.forEach((detail) => {
    const { absenceTypeInfo, status } = detail;

    const entry = {
      id: absenceTypeInfo.id,
      color: absenceTypeInfo.active
        ? groupInfo[absenceTypeInfo.absenceGroupId].color || "#C6D9BE"
        : grey[500],
      order: groupInfo[absenceTypeInfo.absenceGroupId].order,
      name: absenceTypeInfo.name,
      description: absenceTypeInfo.description,
      presenceType: absenceTypeInfo.presenceType,
      status,
      active: absenceTypeInfo.active,
      parentName: "",
    };

    entriesMap[absenceTypeInfo.id] = entry;

    // Only add to the result array if it has no parent (i.e., it's a top-level entry)
    if (!absenceTypeInfo.parentAbsenceTypeId) {
      resultArray.push(entry);
    } else {
      // Otherwise, add it as a child to its parent entry
      const parent = entriesMap[absenceTypeInfo.parentAbsenceTypeId];
      if (parent) {
        entry.parentName = parent.name;
        parent.absenceTypes = parent.absenceTypes || [];
        parent.absenceTypes.push(entry);
      }
    }
  });

  const active = {
    absenceTypes: resultArray
      .filter(
        (type) => type.presenceType === EPresenceType.Absence && type.active
      )
      .sort(sortByGroupOrder),
    attendanceTypes: resultArray
      .filter(
        (type) => type.presenceType === EPresenceType.Attendance && type.active
      )
      .sort(sortByGroupOrder),
  };

  const inactive = {
    absenceTypes: resultArray
      .filter(
        (type) => type.presenceType === EPresenceType.Absence && !type.active
      )
      .sort(sortByGroupOrder),
    attendanceTypes: resultArray
      .filter(
        (type) => type.presenceType === EPresenceType.Attendance && !type.active
      )
      .sort(sortByGroupOrder),
  };

  return { active, inactive };
};
